import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { authApi } from '../api/auth';
import { setAuthToken } from '../api/client';

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_onboarding_done?: boolean;
  email_verified?: boolean;
  created_at?: string;
  last_login?: string;
  subscription_plan?: 'free' | 'mail' | 'template' | 'bundle';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string) => Promise<void>;
  logout: () => void;
  verifyToken: (token: string) => Promise<void>;
  refreshAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Prevent double fetch in React.StrictMode (development)
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Check for token in URL first (from magic link)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');

    if (urlToken) {
      // Token from URL, verify it
      verifyToken(urlToken)
        .then(() => {
          // Remove token from URL
          window.history.replaceState({}, document.title, window.location.pathname);
        })
        .catch(() => {
          // Token verification failed silently
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Check for existing token in localStorage
      const token = localStorage.getItem('authToken');

      if (token) {
        setAuthToken(token);
        authApi
          .getProfile()
          .then((response) => {
            if (response.success) {
              // Backend response format: { success: true, data: { user: {...} } }
              const userData = response.data?.user || response.data;
              setUser(userData);
            }
          })
          .catch((error) => {
            if (error.response?.status === 401) {
              localStorage.removeItem('authToken');
              setAuthToken(null);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setLoading(false);
      }
    }
  }, []);

  const login = async (email: string) => {
    try {
      const response = await authApi.sendMagicLink(email);
      if (response.success) {
        // Magic link sent successfully
        return;
      } else {
        throw new Error(response.error?.message || 'Giriş başarısız');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.error?.message || 'Giriş başarısız');
    }
  };

  const verifyToken = async (token: string) => {
    try {
      // Store token first
      localStorage.setItem('authToken', token);
      setAuthToken(token);

      const profileResponse = await authApi.getProfile();
      if (profileResponse.success) {
        // Backend response format: { success: true, data: { user: {...} } }
        const userData = profileResponse.data?.user || profileResponse.data;
        setUser(userData);
        return;
      } else {
        throw new Error('User profile alınamadı');
      }
    } catch (error: any) {
      // Clean up on error
      localStorage.removeItem('authToken');
      setAuthToken(null);
      throw new Error(error.response?.data?.error?.message || 'Token doğrulama başarısız');
    }
  };

  const refreshAuth = async (): Promise<boolean> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return false;
      }

      // Set the token header
      setAuthToken(token);
      const response = await authApi.getProfile();
      if (response.success) {
        // Backend response format: { success: true, data: { user: {...} } }
        const userData = response.data?.user || response.data;
        setUser(userData);
        return true;
      } else {
        localStorage.removeItem('authToken');
        setAuthToken(null);
        return false;
      }
    } catch {
      localStorage.removeItem('authToken');
      setAuthToken(null);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setAuthToken(null);
    setUser(null);

    authApi.logout();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    verifyToken,
    refreshAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
